import {
  Column,
  ImageFromCdn,
  ProgressiveLoader,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { TrustPilotExtended } from '@/icons/TrustPilotExtended'
import { themeColor, toThemePixels } from '@/utils'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useAssessmentValues } from '../helpers'
import { useEffect, useRef } from 'react'

const DELAY = 2500

const PrepareOfferStepTestimonialContainer = styled(Column)`
  gap: ${toThemePixels(4)};
  padding: ${toThemePixels(4)};
  width: 100%;
  height: 700px;
  position: relative;
`

const StyledImageFromCdnContainer = styled.div`
  position: relative;
  width: 100%;
  height: 370px;
  top: -20px;
  overflow: hidden;
`

const StyledImageFromCdn = styled(ImageFromCdn)`
  height: 100%;
  width: 100%;
  position: absolute;
  object-fit: cover;
`

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    ${themeColor(ThemeColor.b10)},
    rgba(128, 128, 128, 0) 35%,
    rgba(128, 128, 128, 0) 0
  );
  z-index: 1;
`

const useMessages = () => {
  const { t } = useTranslation()

  return {
    steps: [
      t('assessmentLoadingStep1'),
      t('liabilityAssessmentLoadingStep2'),
      t('liabilityAssessmentLoadingStep3'),
      t('assessmentLoadingStep5'),
    ],
    prepareOfferImageTitle: t('prepareOfferImageTitle'),
    prepareOfferImageContent: t('prepareOfferImageContent'),
  }
}

interface PrepareOfferStepProps {
  onNext: () => void
  isPartnerFunnel?: boolean
  poaSigned?: boolean
  transformSubmitValues?: (values: any, poaSigned: boolean) => any
  updateAssessmentMutation?: {
    mutateAsync: (data: any) => Promise<any>
  }
}

export const PrepareOfferStep = ({
  onNext,
  isPartnerFunnel,
  poaSigned,
  transformSubmitValues,
  updateAssessmentMutation,
}: PrepareOfferStepProps) => {
  const messages = useMessages()
  const { values } = useAssessmentValues()
  const isLoading = useRef(false)
  const timeout = useRef<NodeJS.Timeout | null>(null)

  const getUpdateAssessmentMutation = () => {
    if (isPartnerFunnel && poaSigned && !isLoading.current) {
      return updateAssessmentMutation?.mutateAsync(
        transformSubmitValues?.(values, poaSigned),
      )
    }

    return Promise.resolve()
  }

  useEffect(() => {
    const timerPromise = new Promise((resolve) => {
      timeout.current = setTimeout(resolve, DELAY)
    })

    const mutationPromise = getUpdateAssessmentMutation()

    isLoading.current = true

    Promise.all([timerPromise, mutationPromise])
      .then(() => {
        onNext()
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => {
        isLoading.current = false
      })

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current)
      }
    }
  }, [isLoading.current])

  return <ProgressiveLoader totalDelay={DELAY} steps={messages.steps} />
}

export const PrepareOfferStepTestimonial = () => {
  const { t } = useTranslation()

  return (
    <PrepareOfferStepTestimonialContainer>
      <StyledImageFromCdnContainer>
        <GradientOverlay />
        <StyledImageFromCdn name="carrier-waterfall" type="png" />
      </StyledImageFromCdnContainer>
      <Typography color="b100" variant="subheadline">
        <Trans
          i18nKey="prepareOfferTestimonial.title"
          components={[
            <Typography color="info1" variant="subheadline" />,
            <Typography color="info1" variant="subheadline" />,
          ]}
        />
      </Typography>
      <Typography color="b50" variant="description">
        {t('prepareOfferTestimonial.subtitle')}
      </Typography>
      <TrustPilotExtended width={160} />
    </PrepareOfferStepTestimonialContainer>
  )
}
